import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";
import type { MediaMgtData } from "./closureMsg";

type RejectFn = (value: string) => unknown;

const getTemporaryMsgData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/temporaryMessages/${instanceId}?apiName=temporaryMsg`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `temporaryMsg`,
      message: `Something went wrong in getTemporaryMsgData API => ${error}`,
    });
    logMessage(
      `temporaryMsg`,
      `Something went wrong in getTemporaryMsgData API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getTemporaryMsgData api endpoint");
  }
};

export const getTemporaryMsg = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>(
  "temporaryMsg/getTemporaryMsg",
  async (_args, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    return await getTemporaryMsgData(instanceId, rejectWithValue);
  },
);

export const getTemporaryMsgMediaFile = createAsyncThunk<
  MediaMgtData & { mediaContent: unknown },
  MediaMgtData
>(
  "temporaryMsg/getTemporaryMsgMediaFile",
  async (mediaMgtData: MediaMgtData, { rejectWithValue }) => {
    const { instanceId, languageCode, language, fileName, type } = mediaMgtData;
    try {
      const response = await awsClientGateway({
        pathTemplate: `/mediaFile/${instanceId}/${languageCode}-${language}/${fileName}/${type}`,
        method: "GET",
      });
      const result = {
        ...mediaMgtData,
        mediaContent: response.data.mediaContent,
      };
      return result;
    } catch (error) {
      clientLog({
        component: `clsoureMsg`,
        message: `Something went wrong in getTemporaryMsgMediaFile API => ${error}`,
      });
      logMessage(
        `clsoureMsg`,
        `Something went wrong in getTemporaryMsgMediaFile API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in getTemporaryMsgMediaFile api endpoint");
    }
  },
);

export const createOrUpdateTemporaryMsg = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "temporaryMsg/createOrUpdateTemporaryMsg",
  async (temporaryMsgData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/temporaryMessages/${instanceId}`,
        method: "POST",
        body: { ...temporaryMsgData },
      });
      return await getTemporaryMsgData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `temporaryMsg`,
        message: `Something went wrong in createOrUpdateTemporaryMsg API => ${error}`,
      });
      logMessage(
        `temporaryMsg`,
        `Something went wrong in createOrUpdateTemporaryMsg API => ${error}`,
        `error`,
      );
      return rejectWithValue(
        "Error in createOrUpdateTemporaryMsg api endpoint",
      );
    }
  },
);

export const getResetTemporaryMsg = createAction(
  "temporaryMsg/getResetTemporaryMsg",
);
